import { baseApiClient as api } from "../baseService";
export const addTagTypes = ["treatment-sms"] as const;
const injectedRtkApi = api
  .enhanceEndpoints({
    addTagTypes,
  })
  .injectEndpoints({
    endpoints: (build) => ({
      treatmentPreviewSms: build.mutation<
        TreatmentPreviewSmsApiResponse,
        TreatmentPreviewSmsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/treatment/preview-sms/`,
          method: "POST",
          body: queryArg.previewTreatmentSmsRequest,
        }),
        invalidatesTags: ["treatment-sms"],
      }),
      treatmentSendSms: build.mutation<
        TreatmentSendSmsApiResponse,
        TreatmentSendSmsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/treatment/send-sms/`,
          method: "POST",
          body: queryArg.sendTreatmentSmsRequest,
        }),
        invalidatesTags: ["treatment-sms"],
      }),
    }),
    overrideExisting: false,
  });
export { injectedRtkApi as enhancedApi };
export type TreatmentPreviewSmsApiResponse =
  /** status 200  */ PreviewTreatmentSmsResponse;
export type TreatmentPreviewSmsApiArg = {
  previewTreatmentSmsRequest: PreviewTreatmentSmsRequest;
};
export type TreatmentSendSmsApiResponse =
  /** status 200  */ SendTreatmentSmsResponse;
export type TreatmentSendSmsApiArg = {
  sendTreatmentSmsRequest: SendTreatmentSmsRequest;
};
export type ConversationMessageSchema = {
  /** Message ID */
  id: string | string;
  /** Message content/body */
  content: string;
  /** True if from client, False if outbound */
  is_inbound: boolean;
  /** When the message was sent/received */
  source_timestamp: string;
  /** Sender phone number (E.164) */
  from_number?: string | null;
  /** Recipient phone number (E.164) */
  to_number?: string | null;
};
export type PreviewTreatmentSmsResponse = {
  /** Original template content (if template_id provided) */
  original_content?: string | null;
  /** Message content with merge fields substituted */
  rendered_content: string;
  /** Values used for each merge field */
  merge_field_values: {
    [key: string]: string | null;
  };
  /** Merge fields that could not be substituted (still contain {{...}}) */
  unsubstituted_fields?: string[];
  /** Phone number that would be used as recipient */
  recipient_phone?: string | null;
  /** Phone number that would be used as sender (firm.sms_sender_number) */
  sender_phone?: string | null;
  /** Whether firm.sms_sender_number is configured */
  sender_configured: boolean;
  /** Last 10 messages from the client SMS conversation for context */
  recent_messages?: ConversationMessageSchema[];
};
export type PreviewTreatmentSmsRequest = {
  /** The matter to use for merge field context */
  matter_id: string | string;
  /** Template to preview (mutually exclusive with message_text) */
  template_id?: (string | string) | null;
  /** Custom text to preview (for validation) */
  message_text?: string | null;
};
export type TransmissionStatus =
  | "pending"
  | "sending"
  | "sent"
  | "delivered"
  | "failed"
  | "bounced";
export type SendTreatmentSmsResponse = {
  /** ID of the created Message record (None - created by webhook) */
  message_id?: (string | string) | null;
  /** ID of the client SMS conversation (None - created by webhook) */
  conversation_id?: (string | string) | null;
  /** The final message text that was sent */
  message_text: string;
  /** The phone number the SMS was sent to (E.164) */
  sent_to: string;
  /** The sender phone number (firm.sms_sender_number) */
  sent_from: string;
  /** Transmission status (SENT if Dialpad API call succeeded) */
  transmission_status: TransmissionStatus;
  /** Whether the linked task was updated with send note */
  task_updated?: boolean;
  /** Whether Slack notification was sent to team channel */
  slack_notified?: boolean;
};
export type SendTreatmentSmsRequest = {
  /** The matter to send SMS for */
  matter_id: string | string;
  /** SMS template to use (mutually exclusive with message_text) */
  template_id?: (string | string) | null;
  /** Custom message text (mutually exclusive with template_id) */
  message_text?: string | null;
  /** Override recipient phone number (E.164 format). If not provided, uses primary client phone. */
  recipient_phone?: string | null;
  /** Link to treatment check-in task for update logging */
  task_id?: (string | string) | null;
};
export const { useTreatmentPreviewSmsMutation, useTreatmentSendSmsMutation } =
  injectedRtkApi;
